import { formatIsoDate, hoursBetweenTime } from './date';

export type DayKey =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type DayHours = {
  from: string;
  to: string;
};

export type WeeklyHours = Record<DayKey, DayHours[]>;

export type ScheduleFormValues = {
  startDate: Date;
  weeklyHours: WeeklyHours;
};

export type ScheduleLabels = {
  from: string;
  to: string;
  days: Record<DayKey, string>;
};

type WorkingTimeDay = {
  dewt_weekday: number;
  dewt_start_time: string;
  dewt_end_time: string;
};

type WorkingTimeSchedule = {
  dwts_start: string;
  days: WorkingTimeDay[];
};

export const DAY_KEYS: DayKey[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const VIEW_WEEKDAYS = [
  { key: 'monday', weekday: 1 },
  { key: 'tuesday', weekday: 2 },
  { key: 'wednesday', weekday: 3 },
  { key: 'thursday', weekday: 4 },
  { key: 'friday', weekday: 5 },
] as const;

export function createEmptyWeeklyHours(): WeeklyHours {
  return DAY_KEYS.reduce<WeeklyHours>((acc, day) => {
    acc[day] = [];
    return acc;
  }, {} as WeeklyHours);
}

export function createEmptyDayHours(): DayHours {
  return { from: '', to: '' };
}

export function formatScheduleDate(value: string): string {
  const [year, month, day] = value.split('-');

  if (!year || !month || !day) {
    return value;
  }

  return `${day}.${month}.${year}`;
}

function formatScheduleTime(value: string | null): string | null {
  if (!value?.trim()) {
    return null;
  }

  return value.slice(0, 5);
}

export function getDayIntervals(days: WorkingTimeDay[]): string[] {
  return days
    .map((day) => ({
      from: formatScheduleTime(day.dewt_start_time),
      to: formatScheduleTime(day.dewt_end_time),
    }))
    .filter(
      (interval): interval is { from: string; to: string } =>
        Boolean(interval.from && interval.to),
    )
    .sort((first, second) => first.from.localeCompare(second.from))
    .map((interval) => `${interval.from} - ${interval.to}`);
}

export function addDays(date: Date, days: number): Date {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  nextDate.setHours(0, 0, 0, 0);
  return nextDate;
}

export function todayDate(): Date {
  return addDays(new Date(), 0);
}

export function maxDate(firstDate: Date, secondDate: Date): Date {
  return firstDate > secondDate ? firstDate : secondDate;
}

export function dateFromDto(value: string | null | undefined): Date {
  if (!value) {
    return todayDate();
  }

  const [year, month, day] = value.split('-').map(Number);

  if (!year || !month || !day) {
    return todayDate();
  }

  return new Date(year, month - 1, day);
}

export function isScheduleDateOnOrBeforeToday(value: string): boolean {
  return formatIsoDate(dateFromDto(value)) <= formatIsoDate(todayDate());
}

export function getScheduleMinStartDate(
  currentSchedule: WorkingTimeSchedule | null,
): Date {
  if (!currentSchedule) {
    return todayDate();
  }

  return addDays(dateFromDto(currentSchedule.dwts_start), 1);
}

function normalizeTime(value: string | null | undefined): string {
  return value?.trim().slice(0, 5) ?? '';
}

export function scheduleToWeeklyHours(
  schedule: WorkingTimeSchedule | null,
): WeeklyHours {
  const weeklyHours = createEmptyWeeklyHours();

  schedule?.days.forEach((day) => {
    const weekday = VIEW_WEEKDAYS.find(
      (item) => item.weekday === day.dewt_weekday,
    );

    if (!weekday) {
      return;
    }

    const from = normalizeTime(day.dewt_start_time);
    const to = normalizeTime(day.dewt_end_time);
    if (from && to) {
      weeklyHours[weekday.key].push({ from, to });
    }
  });

  VIEW_WEEKDAYS.forEach(({ key }) => {
    weeklyHours[key].sort((first, second) =>
      first.from.localeCompare(second.from),
    );
  });

  return weeklyHours;
}

function normalizeWeeklyHours(weeklyHours: WeeklyHours) {
  return VIEW_WEEKDAYS.map(({ key }) => ({
    key,
    intervals: (weeklyHours[key] ?? [])
      .map((interval) => ({
        from: normalizeTime(interval.from),
        to: normalizeTime(interval.to),
      }))
      .filter((interval) => interval.from || interval.to)
      .sort((first, second) => first.from.localeCompare(second.from)),
  }));
}

function weeklyHoursMatchSchedule(
  weeklyHours: WeeklyHours,
  schedule: WorkingTimeSchedule | null,
): boolean {
  return (
    JSON.stringify(normalizeWeeklyHours(weeklyHours)) ===
    JSON.stringify(normalizeWeeklyHours(scheduleToWeeklyHours(schedule)))
  );
}

export function hasInvalidIntervals(weeklyHours: WeeklyHours): boolean {
  return normalizeWeeklyHours(weeklyHours).some(({ intervals }) => {
    const hasInvalidInterval = intervals.some((interval) => {
      if (!interval.from || !interval.to) {
        return true;
      }

      return interval.from >= interval.to;
    });

    if (hasInvalidInterval) {
      return true;
    }

    const sortedIntervals = [...intervals].sort((first, second) =>
      first.from.localeCompare(second.from),
    );
    return sortedIntervals.some(
      (interval, index) =>
        index > 0 && sortedIntervals[index - 1].to > interval.from,
    );
  });
}

export function calculateTotalHours(weeklyHours: WeeklyHours): number {
  return normalizeWeeklyHours(weeklyHours).reduce(
    (total, { intervals }) =>
      total +
      intervals.reduce((dayHours, interval) => {
        if (!interval.from || !interval.to || interval.from >= interval.to) {
          return dayHours;
        }

        return dayHours + hoursBetweenTime(interval.from, interval.to);
      }, 0),
    0,
  );
}

export function calculateDayHours(intervals: DayHours[]): number {
  return intervals.reduce((total, interval) => {
    const from = normalizeTime(interval.from);
    const to = normalizeTime(interval.to);
    if (!from || !to || from >= to) {
      return total;
    }

    return total + hoursBetweenTime(from, to);
  }, 0);
}

export function formatWorkingHours(hours: number): string {
  const minutes = Math.round(hours * 60);
  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = Math.abs(minutes % 60);
  return `${wholeHours}:${remainingMinutes.toString().padStart(2, '0')}`;
}

export function durationToHours(value: string | null | undefined): number {
  if (!value) {
    return 0;
  }

  const match = value.trim().match(/^(?:(\d+)\s+)?(\d+):(\d+)(?::\d+(?:\.\d+)?)?$/);
  if (!match) {
    return 0;
  }

  const [, days = '0', hours = '0', minutes = '0'] = match;
  return Number(days) * 24 + Number(hours) + Number(minutes) / 60;
}

export function scheduleFormToPayload(values: ScheduleFormValues) {
  const days = VIEW_WEEKDAYS.flatMap(({ key, weekday }) =>
    (values.weeklyHours[key] ?? [])
      .map((interval) => ({
        from: normalizeTime(interval.from),
        to: normalizeTime(interval.to),
      }))
      .filter((interval) => interval.from && interval.to)
      .sort((first, second) => first.from.localeCompare(second.from))
      .map((interval) => ({
        dewt_weekday: weekday,
        dewt_start_time: interval.from,
        dewt_end_time: interval.to,
      })),
  );

  return {
    dwts_start: formatIsoDate(values.startDate),
    days,
  };
}

export function isUnchangedSchedule(
  values: ScheduleFormValues,
  currentSchedule: WorkingTimeSchedule | null,
  futureSchedule: WorkingTimeSchedule | null,
): boolean {
  if (futureSchedule) {
    return (
      formatIsoDate(values.startDate) === futureSchedule.dwts_start &&
      weeklyHoursMatchSchedule(values.weeklyHours, futureSchedule)
    );
  }

  return weeklyHoursMatchSchedule(values.weeklyHours, currentSchedule);
}
